import React, { useState } from 'react';
import { Box, Card, CardContent, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { IconCopy, IconCheck, IconTrash, IconEdit, IconPhone, IconLink } from '@tabler/icons';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import ListOutlinedIcon from '@mui/icons-material/ListOutlined';

// Prévia estilo WhatsApp de um conjunto de botões/lista - sem nenhum
// conceito de aprovação Meta (diferente de `TemplatePreviewCard.js`),
// já que mensagem interativa livre não passa por esse fluxo. Botões são
// sempre resposta rápida simples (nem official nem w_api têm suporte de
// verdade a botão de ligar/abrir link fora de Template Message aprovado).
const WhatsappInteractivePreviewCard = ({ template, onEdit, onDelete }) => {
    const {
        name,
        kind,
        body_text: bodyText,
        buttons = [],
        list_button_text: listButtonText,
        list_title: listTitle,
        list_footer_text: listFooterText,
        list_sections: listSections = [],
        carousel_cards: carouselCards = []
    } = template;
    const [copied, setCopied] = useState(false);

    const handleCopyId = async () => {
        try {
            await navigator.clipboard.writeText(template.id);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Card sx={{ borderRadius: 4 }}>
            <CardContent>
                {name && (
                    <Typography variant="h6" fontWeight="700" mb={1}>
                        {name}
                    </Typography>
                )}

                {template.id && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mb: 1.5,
                            bgcolor: 'rgba(0,0,0,0.04)',
                            borderRadius: '8px',
                            px: 1,
                            py: 0.5
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', color: 'text.secondary', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            ID: {template.id}
                        </Typography>
                        <Tooltip title={copied ? 'Copiado!' : 'Copiar ID (use no body do endpoint de envio)'}>
                            <IconButton size="small" onClick={handleCopyId}>
                                {copied ? <IconCheck size={14} color="#2e7d32" /> : <IconCopy size={14} />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                <Box sx={{ bgcolor: '#d9fdd3', borderRadius: '8px 8px 0 0', p: 1.5 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {bodyText || 'Sua mensagem aparecerá aqui...'}
                    </Typography>
                </Box>

                {kind === 'buttons' && buttons.length > 0 && (
                    <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderTop: 0, borderRadius: '0 0 8px 8px', mb: 1 }}>
                        {buttons.map((btn, i) => (
                            <Box
                                key={i}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                sx={{ py: 1, borderTop: i > 0 ? '1px solid rgba(0,0,0,0.08)' : undefined, color: '#0091ea' }}
                            >
                                <ReplyOutlinedIcon fontSize="small" />
                                <Typography variant="body2">{btn.title || btn.buttonText}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {kind === 'list' && (
                    <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderTop: 0, borderRadius: '0 0 8px 8px', mb: 1 }}>
                        {listTitle && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pt: 1 }}>
                                {listTitle}
                            </Typography>
                        )}
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ py: 1, color: '#0091ea' }}>
                            <ListOutlinedIcon fontSize="small" />
                            <Typography variant="body2">{listButtonText || 'Ver opções'}</Typography>
                        </Box>
                        {listSections.length > 0 && (
                            <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', px: 1.5, py: 1 }}>
                                {listSections.map((section, si) => (
                                    <Box key={si} mb={si < listSections.length - 1 ? 1 : 0}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="700">
                                            {section.title}
                                        </Typography>
                                        {(section.rows || []).map((row, ri) => (
                                            <Box key={ri} sx={{ py: 0.5 }}>
                                                <Typography variant="body2">{row.title}</Typography>
                                                {row.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {listFooterText && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pb: 1 }}>
                                {listFooterText}
                            </Typography>
                        )}
                    </Box>
                )}

                {kind === 'carousel' && carouselCards.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 1 }}>
                        {carouselCards.map((card, ci) => (
                            <Box
                                key={ci}
                                sx={{
                                    minWidth: 160,
                                    maxWidth: 160,
                                    flexShrink: 0,
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    bgcolor: '#fff'
                                }}
                            >
                                {card.image ? (
                                    <Box component="img" src={card.image} sx={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                                ) : (
                                    <Box sx={{ width: '100%', height: 90, bgcolor: 'rgba(0,0,0,0.05)' }} />
                                )}
                                <Box sx={{ p: 1 }}>
                                    <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', mb: 0.5 }}>
                                        {card.text}
                                    </Typography>
                                    {(card.buttons || []).map((btn, bi) => (
                                        <Box
                                            key={bi}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            gap={0.5}
                                            sx={{ py: 0.5, borderTop: '1px solid rgba(0,0,0,0.08)', color: '#0091ea' }}
                                        >
                                            {btn.type === 'CALL' && <IconPhone size={12} />}
                                            {btn.type === 'URL' && <IconLink size={12} />}
                                            {btn.type === 'REPLAY' && <ReplyOutlinedIcon sx={{ fontSize: 12 }} />}
                                            <Typography variant="caption">{btn.buttonText}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {template.id && (onEdit || onDelete) && (
                    <Stack direction="row" spacing={1} mt={2}>
                        {onEdit && (
                            <IconButton size="small" onClick={() => onEdit(template)}>
                                <IconEdit size={16} />
                            </IconButton>
                        )}
                        {onDelete && (
                            <IconButton size="small" color="error" onClick={() => onDelete(template.id)}>
                                <IconTrash size={16} />
                            </IconButton>
                        )}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
};

export default WhatsappInteractivePreviewCard;
